import { Context, Controller } from '@hotwired/stimulus';
import { FocusKeys, focusZone } from '@primer/behaviors';
import { emit } from 'src/emitter';
import { toggleAttribute, toggleHidden } from 'src/utils/dom-toggle';
import { onTransitionEnd } from 'src/utils/dom-transition';
import { requestSubmit } from 'src/utils/form';

export const EVENT_CHANGED_ASSIGNED = 'task/assigned';

/**
 * Enables the assigned user dialog for an individual todo
 */
export default class TodosAssignedController extends Controller {
  public static targets = ['editButton', 'modal', 'assigned'];
  public static values = {
    url: String,
  };

  declare private readonly modalTarget: HTMLElement;
  declare private readonly editButtonTarget: HTMLButtonElement;
  declare private readonly assignedTarget: HTMLElement;
  declare private readonly urlValue: string;

  private inflated: boolean = false;
  private focusController: AbortController | undefined;

  constructor(context: Context) {
    super(context);

    this.onKey = this.onKey.bind(this);
    this.onOutsideClick = this.onOutsideClick.bind(this);
    this.onChanged = this.onChanged.bind(this);
  }

  public connect(): void {
    this.inflate();
    document.addEventListener(EVENT_CHANGED_ASSIGNED, this.onChanged);
  }

  public disconnect(): void {
    this.inflated = false;

    document.removeEventListener(EVENT_CHANGED_ASSIGNED, this.onChanged);
  }

  get current(): string | null {
    return (
      this.assignedTarget
        .querySelector('[data-todos--assigned-target="user"]')
        ?.getAttribute('data-user-id') || null
    );
  }

  get opened(): boolean {
    return this.editButtonTarget.getAttribute('aria-expanded') === 'true';
  }

  get disabled(): boolean {
    return this.editButtonTarget.disabled;
  }

  set opened(next: boolean) {
    this.editButtonTarget.setAttribute('aria-expanded', String(next));

    // this.inflate();

    if (this.inflated) {
      if (next) {
        this.show();
      } else {
        this.hide();
      }
    }
  }

  public onToggle(): void {
    if (this.disabled) {
      return;
    }

    this.opened = !this.opened;

    // Submit on close
    if (!this.opened) {
      requestSubmit(this.modalTarget.querySelector('form'));
    }
  }

  public startLoading() {
    if (this.disabled) {
      return;
    }

    this.editButtonTarget.disabled = true;
  }

  public stopLoading() {
    this.editButtonTarget.disabled = false;
  }

  public onReplaced(
    event: Event & { detail: [unknown, unknown, XMLHttpRequest] },
  ): void {
    const [, , xhr] = event.detail;
    const contentType = (
      xhr.getResponseHeader('content-type') ?? 'text/html'
    ).split(';')[0];
    this.opened = false;

    // This is for backwards compatibility
    if (contentType === 'text/html') {
      this.assignedTarget.innerHTML = xhr.response as string;
      this.assignedTarget
        .querySelectorAll<HTMLButtonElement>(
          `
  button[type="button"][data-todos--assigned-target="warning-requirement"],
  button[type="button"][data-todos--assigned-target="warning-access"]
        `.trim(),
        )
        .forEach((warningButton) => {
          warningButton.click();
        });

      emit('todos:assigned', { value: this.current });
    }

    this.stopLoading();
  }

  private onKey(e: KeyboardEvent): void {
    if (!this.opened) {
      return;
    }

    switch (e.key) {
      case 'Escape': {
        e.preventDefault();
        this.onToggle();
        return;
      }

      case 'Tab': {
        e.preventDefault();
        this.onToggle();
        return;
      }
    }
  }

  private onOutsideClick(e: MouseEvent): void {
    if (e.target instanceof HTMLElement) {
      if (this.modalTarget == e.target || this.modalTarget.contains(e.target)) {
        return;
      }
    }

    this.onToggle();
  }

  private onChanged(event: Event): void {
    if (
      !('detail' in event) ||
      typeof event.detail !== 'object' ||
      !event.detail
    ) {
      throw new Error('Expected custom event with detail property');
    }

    const stimulusAction = event as Omit<CustomEvent, 'detail'> & {
      detail: {
        userId: string;
      };
    };
    if (!('userId' in stimulusAction.detail)) {
      throw new Error(
        'Expected userId in detail, actual: ' +
          Object.keys(stimulusAction.detail).toString(),
      );
    }

    const { userId } = stimulusAction.detail;

    this.dispatch(EVENT_CHANGED_ASSIGNED, { detail: { userId } });

    if (!this.inflated) {
      return;
    }

    const currentChecked = this.modalTarget.querySelector('input:checked');
    if (currentChecked) {
      toggleAttribute('checked', currentChecked, false);
    }

    const nextChecked = this.modalTarget.querySelector(
      `input[value="${userId}"]`,
    );
    if (nextChecked) {
      toggleAttribute('checked', nextChecked, true);
    }
  }

  private inflate(): void {
    if (this.inflated) {
      return;
    }

    const expected = 'text/vnd.kaboom.dialog.inflate';

    this.startLoading();

    void fetch(this.urlValue, {
      headers: { accept: `${expected}, application/problem+json; q=0.1` },
    })
      .then((response) => {
        if (!response.ok) {
          // TODO: read problem
          return response.text().then((error) => {
            throw new Error(
              `Failed to retrieve inflated dialog on ${this.urlValue}: ${error}`,
            );
          });
        }

        const contentType = response.headers.get('content-type') || '';

        if (contentType.startsWith(expected)) {
          return response.text();
        }

        if (contentType.includes(`variant=${expected}`)) {
          return response.text();
        }

        throw new Error(`Expected ${expected}, actual ${contentType}`);
      })
      .then((html) => {
        this.modalTarget.innerHTML = html;
        this.inflated = true;

        /* const form = this.modalTarget.querySelector('form');
         if (form) {
          form.addEventListener('ajax:beforeSend', (event) => {
            if ('detail' in event) {
              const [xhr, options] = event.detail as [
                XMLHttpRequest,
                Record<string, unknown>,
              ];
              xhr.setRequestHeader('Accept', 'text/html; q=0.5');
            }

            return true;
          });
        } */

        // If still opened, show it
        if (this.opened) {
          this.modalTarget.getBoundingClientRect();
          this.show();
        }
      })
      .finally(() => {
        this.stopLoading();
      });
  }

  private show(): void {
    /**
     * Entering: "transition ease-out duration-100"
     *     From: "transform opacity-0 scale-95"
     *       To: "transform opacity-100 scale-100"
     *  Leaving: "transition ease-in duration-75"
     *     From: "transform opacity-100 scale-100"
     *       To: "transform opacity-0 scale-95"
     */

    // Remove animation (and force render)
    this.modalTarget.classList.remove('transition', 'ease-in', 'duration-75');
    this.modalTarget.getBoundingClientRect();

    // Remove "leaving" and "leaving.to"
    toggleHidden(this.modalTarget, false);
    this.modalTarget.classList.remove('transform', 'opacity-0', 'scale-95');

    // Position correctly
    // this.recalculatePosition();

    // Add "entering" and "entering.from" (and force render)
    this.modalTarget.classList.add('transform', 'opacity-0', 'scale-95');
    this.modalTarget.getBoundingClientRect();

    // Add back animation
    this.modalTarget.classList.add('transition', 'ease-out', 'duration-100');

    requestAnimationFrame(() => {
      if (!this.opened) {
        return;
      }

      // Remove "entering.from" and add "entering.to"
      this.modalTarget.classList.remove('opacity-0', 'scale-95');
      this.modalTarget.classList.add('opacity-100', 'scale-100');

      document.addEventListener('keydown', this.onKey);
      document.addEventListener('click', this.onOutsideClick);

      this.focusController = focusZone(this.modalTarget, {
        focusInStrategy: 'closest',
        focusOutBehavior: 'wrap',
        bindKeys: FocusKeys.ArrowVertical | FocusKeys.HomeAndEnd,
      });

      this.modalTarget.querySelector<HTMLElement>('[tabindex]')?.focus();
    });
  }

  private hide(): void {
    this.focusController?.abort('Assigned modal is hidden');

    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('click', this.onOutsideClick);

    /**
     * Entering: "transition ease-out duration-100"
     *     From: "transform opacity-0 scale-95"
     *       To: "transform opacity-100 scale-100"
     *  Leaving: "transition ease-in duration-75"
     *     From: "transform opacity-100 scale-100"
     *       To: "transform opacity-0 scale-95"
     */

    // Enable animation (and force render)
    this.modalTarget.classList.remove('ease-out', 'duration-100');
    this.modalTarget.classList.add('transition', 'ease-in', 'duration-75');
    this.modalTarget.getBoundingClientRect();

    // Remove "entering" and "entering.to"
    // Add "leaving" and "leaving.from"
    this.modalTarget.classList.add('opacity-100', 'scale-100');

    requestAnimationFrame(() => {
      if (this.opened) {
        return;
      }

      // Remove "leaving.from" and add "leaving.to"
      this.modalTarget.classList.remove('opacity-100', 'scale-100');
      this.modalTarget.classList.add('opacity-0', 'scale-95');

      this.editButtonTarget.focus();

      onTransitionEnd(this.modalTarget, 100, () => {
        if (this.opened) {
          return;
        }

        toggleHidden(this.modalTarget, true);
      });
    });
  }
}
